import axios from 'axios';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import {
    GETREQUESTOPTIONS,
    getDeparimentInfo,
    type DepartmentInfo,
} from '../services/apiService';

interface OnboardingContextValue {
    department: DepartmentInfo | null;
    currentUserId: string | null;
    loading: boolean;
    error: boolean;
    refetch: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export const OnboardingContextProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const [department, setDepartment] = useState<DepartmentInfo | null>(null);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchDepartment = useCallback(async () => {
        const data = await getDeparimentInfo();
        setDepartment(data);
    }, []);

    const fetchCurrentUser = useCallback(async () => {
        const res = await axios.get('/api/user/info', GETREQUESTOPTIONS());
        setCurrentUserId(res.data?.user?._id ?? null);
    }, []);

    const loadAll = useCallback(async () => {
        setError(false);
        setLoading(true);
        try {
            await Promise.all([fetchDepartment(), fetchCurrentUser()]);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [fetchDepartment, fetchCurrentUser]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    return (
        <OnboardingContext.Provider
            value={{
                department,
                currentUserId,
                loading,
                error,
                refetch: fetchDepartment,
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const ctx = useContext(OnboardingContext);
    if (ctx === null) {
        throw new Error(
            'useOnboarding must be used within an OnboardingContextProvider'
        );
    }
    return ctx;
};

export default OnboardingContext;
